import React, { useEffect, useState } from "react";
import { db } from "../config"; // Adjust if needed
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import NavBar from "./NavBar"; // Adjust path if needed

function AdminPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [compoundingPlan, setCompoundingPlan] = useState({
    amount: "",
    duration: "",
  });
  const [savingCompound, setSavingCompound] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "investmentPlans"));
      const plansList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPlans(plansList);
    } catch (err) {
      console.error("Error fetching plans:", err);
      alert("Error fetching investment plans.");
    }
    setLoading(false);
  };

  const fetchCompoundingPlan = async () => {
    try {
      const docRef = doc(db, "settings", "compoundingPlan");
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        setCompoundingPlan(snapshot.data());
      }
    } catch (err) {
      console.error("Failed to fetch compounding plan:", err);
    }
  };

  const handleUpdate = async (id) => {
    const planToUpdate = plans.find((plan) => plan.id === id);
    setUpdatingId(id);
    try {
      const planRef = doc(db, "investmentPlans", id);
      await updateDoc(planRef, {
        name: planToUpdate.name,
        min: Number(planToUpdate.min),
        max: Number(planToUpdate.max),
        roi: Number(planToUpdate.roi),
        duration: Number(planToUpdate.duration),
      });
      alert("Plan updated successfully.");
    } catch (err) {
      console.error("Error updating plan:", err);
      alert("Failed to update plan.");
    }
    setUpdatingId(null);
  };

  const handleChange = (id, field, value) => {
    setPlans((prev) =>
      prev.map((plan) =>
        plan.id === id ? { ...plan, [field]: value } : plan
      )
    );
  };

  const handleUpdateCompoundingPlan = async () => {
    setSavingCompound(true);
    try {
      const docRef = doc(db, "settings", "compoundingPlan");
      await setDoc(docRef, compoundingPlan);
      alert("Compounding plan updated.");
    } catch (err) {
      console.error("Failed to update compounding plan:", err);
      alert("Error updating compounding plan.");
    }
    setSavingCompound(false);
  };

  useEffect(() => {
    fetchPlans();
    fetchCompoundingPlan();
  }, []);

  return (
    <div className="container mt-5">
      {/* Top Navbar */}
      <nav className="main-header navbar navbar-expand navbar-white navbar-light mb-3">
        <ul className="navbar-nav">
          <li className="nav-item">
            <a
              className="nav-link"
              data-widget="pushmenu"
              href="#"
              role="button"
            >
              <i className="fas fa-bars" />
            </a>
          </li>
        </ul>
      </nav>

      {/* Custom Navigation Bar */}
      <NavBar />

      <h3 className="text-center mb-4">Manage Investment Plans</h3>

      {loading ? (
        <p className="text-center">Loading plans...</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle text-center">
            <thead className="table-dark">
              <tr>
                <th>Name</th>
                <th>Min</th>
                <th>Max</th>
                <th>ROI (%)</th>
                <th>Duration (days)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    <input
                      className="form-control"
                      value={plan.name}
                      onChange={(e) =>
                        handleChange(plan.id, "name", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control"
                      type="number"
                      value={plan.min}
                      onChange={(e) =>
                        handleChange(plan.id, "min", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control"
                      type="number"
                      value={plan.max}
                      onChange={(e) =>
                        handleChange(plan.id, "max", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control"
                      type="number"
                      value={plan.roi}
                      onChange={(e) =>
                        handleChange(plan.id, "roi", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control"
                      type="number"
                      value={plan.duration}
                      onChange={(e) =>
                        handleChange(plan.id, "duration", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleUpdate(plan.id)}
                      disabled={updatingId === plan.id}
                    >
                      {updatingId === plan.id ? "Updating..." : "Update"}
                    </button>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">
                    No plans found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Compounding Plan Section */}
      <hr className="my-5" />
      <h4 className="text-center mb-3">Compounding Plan Settings</h4>

      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="mb-3">
            <label className="form-label">Amount</label>
            <input
              type="text"
              className="form-control"
              value={compoundingPlan.amount}
              onChange={(e) =>
                setCompoundingPlan((prev) => ({
                  ...prev,
                  amount: e.target.value,
                }))
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Duration</label>
            <input
              type="text"
              className="form-control"
              value={compoundingPlan.duration}
              onChange={(e) =>
                setCompoundingPlan((prev) => ({
                  ...prev,
                  duration: e.target.value,
                }))
              }
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleUpdateCompoundingPlan}
            disabled={savingCompound}
          >
            {savingCompound ? "Saving..." : "Save Compounding Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminPlansPage;


// import React, { useEffect, useState } from "react";
// import { db } from "../config"; // Adjust as needed
// import {
//   collection,
//   getDocs,
//   updateDoc,
//   doc,
// } from "firebase/firestore";
// import NavBar from "./NavBar"; // Ensure this path is correct

// function AdminPlansPage() {
//   const [plans, setPlans] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [updatingId, setUpdatingId] = useState(null);

//   const fetchPlans = async () => {
//     setLoading(true);
//     try {
//       const snapshot = await getDocs(collection(db, "investmentPlans"));
//       const plansList = snapshot.docs.map((doc) => ({
//         id: doc.id,
//         ...doc.data(),
//       }));
//       setPlans(plansList);
//     } catch (err) {
//       console.error("Error fetching plans:", err);
//       alert("Error fetching investment plans.");
//     }
//     setLoading(false);
//   };

//   const handleChange = (id, field, value) => {
//     setPlans((prev) =>
//       prev.map((plan) =>
//         plan.id === id ? { ...plan, [field]: value } : plan
//       )
//     );
//   };

//   const handleUpdate = async (id) => {
//     const planToUpdate = plans.find((plan) => plan.id === id);
//     setUpdatingId(id);
//     try {
//       const planRef = doc(db, "investmentPlans", id);
//       await updateDoc(planRef, {
//         name: planToUpdate.name,
//         min: Number(planToUpdate.min),
//         max: Number(planToUpdate.max),
//         roi: Number(planToUpdate.roi),
//         duration: Number(planToUpdate.duration),
//       });
//       alert("Plan updated successfully.");
//     } catch (err) {
//       console.error("Error updating plan:", err);
//       alert("Failed to update plan.");
//     }
//     setUpdatingId(null);
//   };

//   useEffect(() => {
//     fetchPlans();
//   }, []);

//   return (
//     <div className="container mt-5">
//             {/* Top Navbar */}
//             <nav className="main-header navbar navbar-expand navbar-white navbar-light mb-3">
//               <ul className="navbar-nav">
//                 <li className="nav-item">
//                   <a
//                     className="nav-link"
//                     data-widget="pushmenu"
//                     href="#"
//                     role="button"
//                   >
//                     <i className="fas fa-bars" />
//                   </a>
//                 </li>
//               </ul>
//             </nav>
      
//             {/* Custom Navigation Bar */}
//             <NavBar />
//       <h3 className="text-center mb-4">Manage Investment Plans</h3>
//       {loading ? (
//         <p className="text-center">Loading plans...</p>
//       ) : (
//         <div className="table-responsive">
//           <table className="table table-bordered table-hover align-middle text-center">
//             <thead className="table-dark">
//               <tr>
//                 <th>Name</th>
//                 <th>Min</th>
//                 <th>Max</th>
//                 <th>ROI (%)</th>
//                 <th>Duration (days)</th>
//                 <th>Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {plans.map((plan) => (
//                 <tr key={plan.id}>
//                   <td>
//                     <input
//                       className="form-control"
//                       value={plan.name}
//                       onChange={(e) =>
//                         handleChange(plan.id, "name", e.target.value)
//                       }
//                     />
//                   </td>
//                   <td>
//                     <input
//                       className="form-control"
//                       type="number"
//                       value={plan.min}
//                       onChange={(e) =>
//                         handleChange(plan.id, "min", e.target.value)
//                       }
//                     />
//                   </td>
//                   <td>
//                     <input
//                       className="form-control"
//                       type="number"
//                       value={plan.max}
//                       onChange={(e) =>
//                         handleChange(plan.id, "max", e.target.value)
//                       }
//                     />
//                   </td>
//                   <td>
//                     <input
//                       className="form-control"
//                       type="number"
//                       value={plan.roi}
//                       onChange={(e) =>
//                         handleChange(plan.id, "roi", e.target.value)
//                       }
//                     />
//                   </td>
//                   <td>
//                     <input
//                       className="form-control"
//                       type="number"
//                       value={plan.duration}
//                       onChange={(e) =>
//                         handleChange(plan.id, "duration", e.target.value)
//                       }
//                     />
//                   </td>
//                   <td>
//                     <button
//                       className="btn btn-sm btn-success"
//                       onClick={() => handleUpdate(plan.id)}
//                       disabled={updatingId === plan.id}
//                     >
//                       {updatingId === plan.id ? "Updating..." : "Update"}
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//               {plans.length === 0 && (
//                 <tr>
//                   <td colSpan="6" className="text-center">
//                     No plans found.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       )}
//     </div>
//   );
// }

// export default AdminPlansPage;
